/**
 * A FICHA: o cadastro da entidade em modo de leitura, montado do próprio descriptor.
 *
 * Existe porque a tela de leitura nascia órfã. Vindo da busca, o operador abria uma pessoa e via
 * duas caixas vazias — "sem correlações", "sem conexões" — e nada do que ele tinha acabado de
 * encontrar: nem o nome, nem o CPF, nem o telefone. O dado do cadastro só aparecia se ele clicasse
 * em «Abrir para editar», ou seja, só entrando num formulário que ele não veio preencher.
 *
 * Duas decisões de projeto:
 *
 * **Vem do descriptor, não de uma lista escrita à mão.** `gruposEdicao` já descreve todo campo,
 * com rótulo e formato. Duplicar isso aqui garantiria que um campo novo aparecesse na edição e
 * nunca na leitura — o tipo de divergência que ninguém percebe até alguém procurar um dado que
 * "sumiu".
 *
 * **Campo vazio não ocupa espaço.** A ficha de pessoa tem ~30 campos e a maioria das importações
 * preenche cinco. Mostrar trinta traços afogaria os cinco que importam. O que existe aparece; o
 * resto fica para a edição, onde a lacuna é a informação útil.
 */
import { useEffect, useState, type ReactNode } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { api } from '@/lib/api';
import { humanizarSeData } from '@/lib/datas';
import { formatar, numeroParaDinheiro } from '@/lib/mascaras';
import type { DefCampo, DescritorRecurso, RecursoBase } from '@/lib/recursos/tipos';

/** Campos que a ficha nunca mostra: ou já são o título, ou são ruído de sistema. */
const OCULTOS = new Set(['id', 'nome', 'razao_social', 'ativo', 'observacoes']);

function valorLegivel(
  campo: DefCampo<RecursoBase>,
  registro: RecursoBase,
  refs: Record<string, string>,
): ReactNode {
  const bruto = registro[campo.chave];
  if (bruto == null || bruto === '') return null;
  if (campo.tipo === 'booleano') return bruto ? 'Sim' : 'Não';
  // `select` guarda o código ('casado'); quem lê quer o rótulo ('Casado(a)').
  if (campo.tipo === 'select') {
    const opcao = campo.opcoes?.find((o) => o.valor === String(bruto));
    return opcao?.rotulo ?? String(bruto);
  }
  // FK: o id não diz nada a ninguém. Resolvido pelo catálogo — e some se não resolver, porque um
  // UUID na tela é pior do que a ausência do campo.
  if (campo.tipo === 'referencia') return refs[String(bruto)] ?? null;
  if (campo.tipo === 'dinheiro') return `R$ ${numeroParaDinheiro(String(bruto))}`;
  const texto = String(bruto);
  const valor = campo.formato ? formatar(texto, campo.formato) : humanizarSeData(texto);
  // O sufixo é derivado do próprio valor (a idade a partir da data). O descriptor já o define para
  // a edição; ler é onde ele mais serve — foi por idade que o operador chegou aqui.
  const sufixo = campo.sufixo?.(texto);
  return sufixo ? `${valor} · ${sufixo}` : valor;
}


/** Catálogos das FKs visíveis, num pedido por endpoint. São tabelas pequenas (198 profissões). */
function useCatalogos(descritor: DescritorRecurso, registro: RecursoBase): Record<string, string> {
  const [refs, setRefs] = useState<Record<string, string>>({});
  const endpoints = [
    ...new Set(
      (descritor.gruposEdicao ?? [])
        .flatMap((g) => g.campos)
        .filter((c) => c.tipo === 'referencia' && c.refEndpoint && registro[c.chave])
        .map((c) => `${c.refEndpoint}|${c.refRotulo ?? 'nome'}`),
    ),
  ];
  const assinatura = endpoints.join(',');

  useEffect(() => {
    if (!assinatura) return;
    let vivo = true;
    void Promise.all(
      assinatura.split(',').map(async (par) => {
        const [endpoint, rotulo] = par.split('|');
        const r = await api.get<RecursoBase[] | { itens?: RecursoBase[] }>(
          `${endpoint}?limite=500`,
        );
        if (!r.ok) return [] as [string, string][];
        // Catálogo devolve ARRAY puro (`/cadastro/profissoes`); lista paginada devolve `{itens}`.
        // Assumir só um dos dois deixava a profissão fora da ficha, em silêncio.
        const itens = Array.isArray(r.dados) ? r.dados : (r.dados.itens ?? []);
        return itens.map((i) => [String(i.id), String(i[rotulo] ?? '')] as [string, string]);
      }),
    ).then((listas) => {
      if (vivo) setRefs(Object.fromEntries(listas.flat()));
    });
    return () => {
      vivo = false;
    };
  }, [assinatura]);

  return refs;
}

export function FichaEntidade({
  descritor,
  registro,
  extras,
}: {
  descritor: DescritorRecurso;
  registro: RecursoBase;
  /** Pares vindos de fora do próprio registro (contatos, endereços — moram em outras tabelas). */
  extras?: { rotulo: string; valores: string[] }[];
}) {
  const refs = useCatalogos(descritor, registro);
  const grupos = (descritor.gruposEdicao ?? [])
    .map((g) => ({
      titulo: g.titulo,
      itens: g.campos
        .filter((c) => !OCULTOS.has(c.chave))
        .map((c) => ({ rotulo: c.rotulo, valor: valorLegivel(c, registro, refs) }))
        .filter((i) => i.valor !== null),
    }))
    .filter((g) => g.itens.length > 0);

  const blocos = (extras ?? []).filter((e) => e.valores.length > 0);

  if (grupos.length === 0 && blocos.length === 0) {
    return (
      <Card>
        <CardContent className="p-6">
          <p className="tipo-corpo-pequeno text-text-tertiary">
            Este cadastro só tem o nome — nenhum outro campo foi preenchido.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="superficie-vidro relative z-[1]">
      <CardHeader>
        <CardTitle>Ficha</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-5">
        {blocos.map((b) => (
          <div key={b.rotulo} className="flex flex-col gap-1">
            <p className="tipo-rotulo">{b.rotulo}</p>
            {b.valores.map((v) => (
              <p key={v} className="tipo-corpo text-text">
                {v}
              </p>
            ))}
          </div>
        ))}
        {grupos.map((g) => (
          <div key={g.titulo} className="flex flex-col gap-2">
            <p className="tipo-rotulo">{g.titulo}</p>
            <dl className="grid gap-x-6 gap-y-2 sm:grid-cols-2 lg:grid-cols-3">
              {g.itens.map((i) => (
                <div key={i.rotulo} className="flex flex-col">
                  <dt className="tipo-corpo-pequeno text-text-tertiary">{i.rotulo}</dt>
                  <dd className="tipo-corpo text-text">{i.valor}</dd>
                </div>
              ))}
            </dl>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
